import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui";
import type { Mark } from "@/lib/attributeReview/stepMarks";
import { isRejectedAttributeReviewStatus } from "@/lib/attributeReview/reviewQueueStatus";

import { ReviewStep } from "./ReviewStep";
import { StepCard } from "./StepCard";
import { StepNote } from "./StepNote";

type Props = {
  mark: Mark;
  summary: string[];
  saveError: string | null;
  showActions: boolean;
  linkMode: boolean;
  required: boolean;
  row: { hidden?: unknown; decisionStatus?: unknown };
  canApprove: boolean;
  working: boolean;
  approving: boolean;
  onApprove: () => void;
  onIgnore: () => void;
  onNotSame: () => void;
  onShowToSeller: () => void;
  onUndoIgnore: () => void;
};

export function SaveStep(p: Props) {
  const hidden = Boolean(p.row.hidden);
  const ignored = isRejectedAttributeReviewStatus(p.row.decisionStatus);
  const status = !p.showActions
    ? "Saved"
    : p.saveError
      ? "Failed"
      : p.canApprove
        ? "Ready"
        : "Waits";
  const secondary = p.linkMode ? (
    <Button variant="outline" disabled={p.working} onClick={p.onNotSame}>
      Not the same fact
    </Button>
  ) : hidden ? (
    <Button variant="outline" disabled={p.working} onClick={p.onShowToSeller}>
      Show to seller
    </Button>
  ) : (
    !p.required && (
      <Button variant="outline" disabled={p.working} onClick={p.onIgnore}>
        Ignore
      </Button>
    )
  );
  return (
    <ReviewStep n={3} mark={p.mark} title="Save" status={status} last>
      <StepCard>
        {p.summary.map((line) => (
          <p
            key={line}
            className="text-[13px] leading-normal text-slate-700 dark:text-slate-200"
          >
            {line}
          </p>
        ))}
        {p.saveError && <StepNote tone="err" title={p.saveError} />}
        {ignored ? (
          <div>
            <Button
              variant="outline"
              disabled={p.working}
              onClick={p.onUndoIgnore}
            >
              Undo ignore
            </Button>
          </div>
        ) : !p.showActions && hidden ? (
          <div>{secondary}</div>
        ) : null}
        {p.showActions && !ignored && (
          <div className="flex items-center gap-2">
            <Button
              disabled={!p.canApprove || p.working}
              title={p.canApprove ? undefined : "Pick a field first"}
              onClick={p.onApprove}
            >
              {p.approving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {p.linkMode ? "Linking…" : hidden ? "Saving…" : "Approving…"}
                </>
              ) : p.linkMode ? (
                "Yes, link them"
              ) : hidden ? (
                "Save & next"
              ) : (
                "Approve & next"
              )}
            </Button>
            {secondary}
          </div>
        )}
      </StepCard>
    </ReviewStep>
  );
}
